import { useState } from 'react';
import { useMapStore } from '../../stores/mapStore';
import { MARKETING_URL } from '../../lib/env';
import { ZoomScalePill } from './ZoomIndicator';
import { GpsAccuracyPill } from './GpsAccuracyPill';
import { CONTRIBUTION_SHEET_BOTTOM, MAP_SHEET_BOTTOM } from './mapSheetOffset';

interface MapAttributionProps {
  zoom?: number;
  center?: { lng: number; lat: number };
  /** Live GPS quality during field mapping (trace or campaign session). */
  fieldMappingGpsAccuracyM?: number | null;
  fieldMappingGpsSkippedCount?: number;
  showFieldMappingGpsPill?: boolean;
}

const GLASS_CHROME =
  'rounded-full bg-white/92 shadow-sm backdrop-blur-sm dark:bg-gray-800/92';

export function MapAttribution({
  zoom,
  center,
  fieldMappingGpsAccuracyM,
  fieldMappingGpsSkippedCount = 0,
  showFieldMappingGpsPill = false,
}: MapAttributionProps) {
  const sheetState = useMapStore((s) => s.sheetState);
  const contributionSheetSnap = useMapStore((s) => s.contributionSheetSnap);
  const contributionSheetChromeBottom = useMapStore((s) => s.contributionSheetChromeBottom);
  const [expanded, setExpanded] = useState(false);

  const contributionChromeActive = contributionSheetSnap != null;
  const measuredChromeBottom = contributionSheetChromeBottom;
  const overlaySheetActive = contributionChromeActive || measuredChromeBottom != null;
  const chromeBottom =
    overlaySheetActive
      ? (measuredChromeBottom
        ?? (contributionSheetSnap
          ? CONTRIBUTION_SHEET_BOTTOM[contributionSheetSnap]
          : MAP_SHEET_BOTTOM.half))
      : MAP_SHEET_BOTTOM[sheetState];

  return (
    <div
      className={`absolute left-3 flex items-center gap-1.5 select-none max-w-[calc(100vw-5.5rem)] ${
        overlaySheetActive ? 'z-[50]' : 'z-[65]'
      }`}
      style={{
        bottom: chromeBottom,
        transition: 'bottom 350ms cubic-bezier(0.32, 0.72, 0, 1)',
      }}
    >
      {zoom !== undefined && (
        <ZoomScalePill zoom={zoom} center={center} className="shrink-0" />
      )}

      {showFieldMappingGpsPill && (
        <GpsAccuracyPill
          accuracyM={fieldMappingGpsAccuracyM}
          skippedCount={fieldMappingGpsSkippedCount}
          className="shrink-0"
        />
      )}

      {expanded ? (
        <div
          className={`flex items-center gap-1.5 pl-2.5 pr-2 py-1 ${GLASS_CHROME} text-[10px] leading-none min-w-0`}
        >
          <a
            href={`${MARKETING_URL}/about`}
            target="_blank"
            rel="noopener noreferrer"
            className="font-medium text-gray-800 dark:text-white hover:text-gray-900 dark:hover:text-white underline transition-colors whitespace-nowrap"
          >
            © Coordal Maps
          </a>
          <span className="text-gray-400 dark:text-gray-500 shrink-0">|</span>
          <a
            href="https://www.openstreetmap.org/copyright"
            target="_blank"
            rel="noopener noreferrer"
            className="text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white underline transition-colors whitespace-nowrap"
          >
            © OpenStreetMap
          </a>
          <button
            type="button"
            onClick={() => setExpanded(false)}
            className="ml-0.5 text-gray-400 hover:text-gray-700 dark:hover:text-white transition-colors shrink-0"
            aria-label="Close attribution"
          >
            ✕
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setExpanded(true)}
          className={`flex h-[22px] w-[22px] items-center justify-center ${GLASS_CHROME} hover:bg-white dark:hover:bg-gray-800/95 transition-colors shrink-0`}
          aria-label="Map attribution — Coordal Maps"
          title="Coordal Maps"
        >
          <img
            src="/logos/coordal-logo.svg"
            alt=""
            className="h-[16px] w-[16px] flex-shrink-0"
          />
        </button>
      )}
    </div>
  );
}
